import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  Fragment,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";

import {
  Activity,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  // CornerDownRight, // used by temporarily hidden unassessed row
  HeartPulse,
  Info,
  Filter,
  LayoutGrid,
  Map as MapIcon,
  ShoppingBag,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import PairsFocusModal, { type PairsFocusModalKind } from "./PairsFocusModal";
import {
  CLUSTER_ORDER,
  CLUSTER_PALETTE,
  CLUSTER_SECTION_META,
  clusterFromAccountHealth,
  type PortfolioClusterKey,
} from "./portfolioClusterStyles";
import {
  buildHeaderInfoTooltips,
  headerInfoIconClass,
  headerTooltipClass,
} from "./portfolioHealthInfo";
import {
  TILE_FILTER_LABELS,
  matchesExecutiveSponsorRole,
  type TileFilter,
} from "./PortfolioSummaryStrip";
import WorkingSetBanner from "./WorkingSetBanner";
import DropdownSelector from "../../Components/DropdownSelector";
import DropdownSwitcher from "../../Components/DropdownSwitcher";
import SearchInput from "../../Components/Search/SearchInput";
import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";
import { useOrgSyntheticDataToggle } from "../../Hooks/useOrgSyntheticDataToggle";
import {
  FLOATING_SELECTION_BAR_PANEL,
  useKpiSelectionBarDock,
} from "../../intelligence-briefing-library/components/hawkeye/KpiCompareSelectionStrip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../intelligence-briefing-library/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../intelligence-briefing-library/components/ui/tooltip";
import type {
  PortfolioAccountRow,
  RelationshipPairRow,
} from "../../Store/Reducers/MyPortfolioOverviewSlice";
import { regionOptions } from "../../Utils/Constants/Constants";
import type {
  AccountHealthThresholds,
  BusinessHealthThresholds,
} from "../../Utils/HealthThresholds";
import { useAccountTiers } from "../../Utils/Helper/accountTierHelpers";
import {
  businessHealthBand,
  businessHealthClass,
} from "../../Utils/Helper/businessHealth";
import { formatCompactCurrency } from "../../Utils/Helper/formatCompactCurrency";
import { formatDateTime, getInitials } from "../../Utils/Helper/Helper";
import { getVendorProfileAvatarBg } from "../../Utils/Helper/profileAvatarUi";
import {
  ACCOUNT_IDENTITY_FALLBACK,
  formatAlLevel,
  getAccountIdentityBadgeStyle,
  UNASSESSED_LEVEL_TITLE,
} from "../Relationship/account-relationships/relationshipUtils";

type ClusterKey = PortfolioClusterKey;
type SortKey =
  | "default"
  | "name-asc"
  | "name-desc"
  | "ah-desc"
  | "renewal-asc"
  | "renewal-desc"
  | "revenue-desc"
  | "revenue-asc"
  | "al-desc"
  | "rl-desc";

const listShellClass = "overflow-hidden rounded-lg bg-gray-50";

type HealthMode = "all" | "ah" | "bh";
type BhBandKey = "concern" | "stable" | "strong" | "no_data";

const BH_BAND_ORDER: BhBandKey[] = ["concern", "stable", "strong", "no_data"];

const BH_BAND_META: Record<
  BhBandKey,
  { label: string; color: string; bg: string; text: string }
> = {
  concern: {
    label: "Concern",
    color: "#ef4444",
    bg: "#fee2e2",
    text: "#dc2626",
  },
  stable: {
    label: "Stable",
    color: "#eab308",
    bg: "#fef9c3",
    text: "#a16207",
  },
  strong: {
    label: "Strong",
    color: "#22c55e",
    bg: "#dcfce7",
    text: "#15803d",
  },
  no_data: {
    label: "Unavailable",
    color: "#9ca3af",
    bg: "#f3f4f6",
    text: "#6b7280",
  },
};

function bhBandFromAccount(score: number | null | undefined): BhBandKey {
  const label = businessHealthClass(score);
  if (label === "Concern") return "concern";
  if (label === "Stable") return "stable";
  if (label === "Strong") return "strong";
  return "no_data";
}

const chipDisabledTooltip = (label: string) =>
  label === "All" ? "No accounts available" : `No accounts in ${label}`;

/** Shared column grid for header row and account cards.
 * Avoid min-w-max — it let long account names expand each card and break
 * alignment. Fixed min width keeps columns lined up; Account takes leftover
 * space; names truncate inside that cell. */
const CARD_GRID =
  "grid w-full min-w-[1390px] grid-cols-[minmax(250px,1fr)_136px_136px_104px_112px_160px_84px_84px_92px] items-center gap-x-5";

const accountCardClass =
  "group w-full cursor-pointer rounded-xl border bg-white px-3.5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:bg-white hover:shadow-[0_4px_14px_rgba(124,58,237,0.08)]";

/** Maps disabled — cards are display-only (no select-to-open-on-Maps). */
const accountCardStaticClass =
  "group w-full rounded-xl border bg-white px-3.5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]";

const accountCardIdleClass = "border-gray-200/90 hover:border-purple-200";
const accountCardSelectedClass =
  "border-purple-400 hover:border-purple-400 bg-white shadow-[0_4px_14px_rgba(124,58,237,0.08)]";

/** Avatar (40px) + gap-2 (8px) — used to pack as many chips as the strip allows. */
const SELECTION_AVATAR_SLOT_PX = 48;
const SELECTION_STRIP_FALLBACK_CAPACITY = 12;
const selectionBarOutlineBtn =
  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-violet-600 bg-white px-2.5 py-1.5 text-[11.5px] font-semibold leading-tight text-violet-700 transition-colors hover:bg-violet-50";

const accountCardsListClass = "flex flex-col gap-3.5 py-2.5 pt-3.5";

const colHeaderClass =
  "text-left text-[11px] font-bold uppercase tracking-wider";

const columnHeaderRowClass = `${CARD_GRID} border-b border-gray-200 bg-gray-100 px-3.5 py-2.5`;

const detailsActionBtnBase =
  "inline-flex h-7 w-full shrink-0 cursor-pointer items-center justify-center gap-0.5 whitespace-nowrap rounded-md border px-1.5 text-[10.5px] font-bold leading-none transition";

const synopsisBtn = `${detailsActionBtnBase} border-purple-200 bg-purple-50 text-purple-700 hover:border-purple-600 hover:bg-purple-600 hover:text-white`;

const assessBtn = `${detailsActionBtnBase} border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-500 hover:bg-amber-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-amber-200 disabled:hover:bg-amber-50 disabled:hover:text-amber-700`;

const HEALTH_BUBBLE_SIZE = 36;

/** Account Health → Triage / Defend / Monitor / Expand (AH score bands). */
const accountHealthBand = (
  score: number | null | undefined,
  accountId?: string | null,
  thresholds?: Partial<AccountHealthThresholds> | null
): { color: string; label: string } | null => {
  if (score == null || !Number.isFinite(score) || score === 0) return null;
  const key = clusterFromAccountHealth(score, accountId, thresholds);
  const palette = CLUSTER_PALETTE[key];
  return { color: palette.color, label: palette.label };
};

/**
 * Business Health = outlined + Concern/Stable/Strong.
 * Account Health = solid + Triage/Defend/Monitor/Expand (same bubble + bottom text layout).
 */
const HealthMetricBubble = ({
  score,
  variant,
  label,
  accountId,
  bhThresholds,
  ahThresholds,
  onClick,
}: {
  score: number | null | undefined;
  variant: "outlined" | "solid";
  label?: string | null;
  accountId?: string | null;
  bhThresholds?: Partial<BusinessHealthThresholds> | null;
  ahThresholds?: Partial<AccountHealthThresholds> | null;
  onClick?: () => void;
}) => {
  const band =
    variant === "outlined"
      ? businessHealthBand(score, bhThresholds)
      : accountHealthBand(score, accountId, ahThresholds);
  const hasScore = band != null;
  const displayLabel = label?.trim() || band?.label || null;
  const color = band?.color ?? "#94a3b8";
  const value = hasScore ? String(Math.round(score as number)) : "–";

  const bubble =
    variant === "outlined" ? (
      <span
        className="inline-flex items-center justify-center rounded-full border-[2.5px] bg-white text-[12px] font-bold tabular-nums"
        style={{
          width: HEALTH_BUBBLE_SIZE,
          height: HEALTH_BUBBLE_SIZE,
          borderColor: color,
          color: hasScore ? color : "#94a3b8",
        }}
      >
        {value}
      </span>
    ) : (
      <span
        className={`inline-flex items-center justify-center rounded-full text-[12px] font-bold tabular-nums ${
          hasScore ? "text-white" : "text-slate-500"
        }`}
        style={{
          width: HEALTH_BUBBLE_SIZE,
          height: HEALTH_BUBBLE_SIZE,
          backgroundColor: hasScore ? color : "#e2e8f0",
        }}
      >
        {value}
      </span>
    );

  const body = (
    <>
      {bubble}
      <div
        className={`text-center text-[11px] font-bold leading-tight ${
          hasScore ? "text-gray-800" : "text-gray-400"
        }`}
      >
        {hasScore ? displayLabel : variant === "solid" ? "Unavailable" : "–"}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          onClick();
        }}
        className="mx-auto flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-md border-none bg-transparent p-0 transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
        aria-label={`Open ${variant === "outlined" ? "Business Health" : "Account Health"} map for this account`}
      >
        {body}
      </button>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col items-center gap-1.5">
      {body}
    </div>
  );
};

function SortableColHeader({
  label,
  activeDir,
  onClick,
}: {
  label: string;
  activeDir: "asc" | "desc" | null;
  onClick: () => void;
}) {
  const Icon =
    activeDir === "asc"
      ? ArrowUp
      : activeDir === "desc"
        ? ArrowDown
        : ArrowUpDown;
  return (
    <button
      type="button"
      className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-left text-[11px] font-bold uppercase tracking-wider text-inherit hover:text-purple-700"
      onClick={onClick}
      aria-label={`Sort by ${label}${
        activeDir ? `, currently ${activeDir}ending` : ""
      }`}
    >
      <span>{label}</span>
      <Icon
        className={`h-3 w-3 shrink-0 ${
          activeDir ? "text-purple-600" : "text-gray-400"
        }`}
        aria-hidden
      />
    </button>
  );
}

function TableHeaderWithInfo({
  label,
  tooltip,
  centered = false,
}: {
  label: string;
  tooltip: ReactNode;
  centered?: boolean;
}) {
  return (
    <Tooltip>
      <div
        className={`inline-flex items-center gap-1 whitespace-nowrap ${centered ? "justify-center" : ""}`}
      >
        <span>{label}</span>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={headerInfoIconClass}
            aria-label={`About ${label}`}
            onClick={e => e.stopPropagation()}
          >
            <Info className="h-2 w-2" strokeWidth={2.5} aria-hidden />
          </button>
        </TooltipTrigger>
      </div>
      <TooltipContent
        side="bottom"
        align={centered ? "center" : "start"}
        sideOffset={8}
        className={headerTooltipClass}
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

const renderSponsorRow = (label: "P" | "S", name?: string | null) => (
  <div className="grid min-w-0 grid-cols-[14px_24px_minmax(0,1fr)] items-center gap-1.5 overflow-hidden py-1">
    <span className="text-[10px] font-bold text-gray-400">{label}</span>
    {name ? (
      <>
        <span
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold text-white"
          style={{ background: getVendorProfileAvatarBg(false) }}
        >
          {getInitials(name)}
        </span>
        <span
          className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-800"
          title={name}
        >
          {name}
        </span>
      </>
    ) : (
      <>
        <span aria-hidden />
        <span className="text-[11px] text-gray-400">—</span>
      </>
    )}
  </div>
);

function accountMatchesSearch(account: PortfolioAccountRow, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const fields = [
    account.customer_name,
    account.primary_sponsors,
    account.secondary_sponsors,
    ...(account.executive_sponsors || []),
  ];
  return fields.some(value => (value || "").toLowerCase().includes(q));
}

const renderSponsorsCell = (account: PortfolioAccountRow) => {
  return (
    <div className="min-w-0 max-w-full overflow-hidden">
      {renderSponsorRow("P", account.primary_sponsors)}
      {renderSponsorRow("S", account.secondary_sponsors)}
    </div>
  );
};

interface Props {
  accounts: PortfolioAccountRow[];
  loading: boolean;
  withOrg: (path: string) => string;
  tileFilter: TileFilter;
  onClearTileFilter: () => void;
  headerFiltersRef?: RefObject<HTMLDivElement | null>;
  prioritiesFilterBarEl?: HTMLDivElement | null;
  onOpenAssess?: (account: PortfolioAccountRow) => void;
  /** False until Izba Admin Portfolio Maps config has loaded (avoids layout flash). */
  mapsConfigReady?: boolean;
  /** Open Maps view with this account selected (and the matching health lens). */
  onOpenAccountOnMap?: (
    accountId: string,
    health: "business_health" | "account_health"
  ) => void;
  /** Open Maps with one or more selected accounts. */
  onOpenAccountsOnMap?: (accountIds: string[]) => void;
  /**
   * Open Relationship Priorities for this account.
   * Pass suppressPairsFocusModal when the pairs modal was already shown here.
   */
  onGoToRelationshipPriorities?: (
    account: PortfolioAccountRow,
    options?: { suppressPairsFocusModal?: boolean }
  ) => void;
  /** Portfolio relationship pairs (for Pairs click scenarios). */
  pairs?: RelationshipPairRow[];
  /** Per-account fan-out cap from org RL settings (Pairs: mapped/cap). */
  fanOutCap?: number;
  workingSetAccountIds?: string[] | null;
  onClearWorkingSet?: () => void;
}

const ATTENTION_BUCKETS = new Set(["unassessed", "no_goal_set", "low_score"]);

const AccountPrioritiesTab = ({
  accounts,
  loading,
  withOrg,
  tileFilter,
  onClearTileFilter,
  headerFiltersRef,
  prioritiesFilterBarEl,
  onOpenAssess,
  mapsConfigReady = false,
  onOpenAccountOnMap,
  onOpenAccountsOnMap,
  onGoToRelationshipPriorities,
  pairs = [],
  fanOutCap,
  workingSetAccountIds,
  onClearWorkingSet,
}: Props) => {
  const navigate = useNavigate();
  const { tierOptions, formatTier } = useAccountTiers();
  const { useSyntheticData } = useOrgSyntheticDataToggle();
  const { businessHealth: bhThresholds, accountHealth: ahThresholds } =
    useHealthScoreThresholds();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTier, setSelectedTier] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedAl, setSelectedAl] = useState("");
  const [selectedCluster, setSelectedCluster] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("default");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [healthMode, setHealthMode] = useState<HealthMode>("all");
  const [selectedBhBand, setSelectedBhBand] = useState<"all" | BhBandKey>(
    "all"
  );
  const [pairsFocus, setPairsFocus] = useState<{
    kind: PairsFocusModalKind;
    account: PortfolioAccountRow;
    label: string;
    attentionCount: number;
    totalCount: number;
    stableCount: number;
  } | null>(null);
  const selectionBoundsRef = useRef<HTMLDivElement>(null);
  const hasSelection = selectedAccountIds.length > 0;
  // Dock to the padded panel so the bar floats at the viewport bottom while
  // scrolling, then settles into the reserved space under the list (no overlap,
  // no large empty gap when content is short).
  const selectionBarDock = useKpiSelectionBarDock(
    selectionBoundsRef,
    hasSelection,
    { insetX: 0, bottomGap: 12 }
  );

  const headerInfoTooltips = useMemo(
    () => buildHeaderInfoTooltips(bhThresholds, ahThresholds),
    [bhThresholds, ahThresholds]
  );

  const filtered = useMemo(() => {
    let list = accounts;
    if (workingSetAccountIds) {
      const ids = new Set(workingSetAccountIds);
      list = list.filter(a => ids.has(a.customer_id));
    }
    if (tileFilter === "attention") {
      list = list.filter(a => a.needs_attention);
    } else if (tileFilter === "primary") {
      list = list.filter(a => (a.role_type || "").toLowerCase() === "primary");
    } else if (tileFilter === "secondary") {
      list = list.filter(
        a => (a.role_type || "").toLowerCase() === "secondary"
      );
    } else if (tileFilter === "executive") {
      list = list.filter(a => matchesExecutiveSponsorRole(a.role_type));
    }
    if (selectedTier) list = list.filter(a => a.tier === selectedTier);
    if (selectedRegion) list = list.filter(a => a.region === selectedRegion);
    if (selectedAl === "none") {
      list = list.filter(a => a.al_level == null);
    } else if (selectedAl) {
      const n = Number(selectedAl);
      list = list.filter(a => a.al_level === n);
    }
    if (healthMode === "ah" && selectedCluster !== "all") {
      list = list.filter(
        a =>
          clusterFromAccountHealth(
            a.account_health,
            a.customer_id,
            ahThresholds
          ) === selectedCluster
      );
    }
    if (healthMode === "bh" && selectedBhBand !== "all") {
      list = list.filter(
        a => bhBandFromAccount(a.business_health) === selectedBhBand
      );
    }
    if (searchQuery.trim()) {
      list = list.filter(a => accountMatchesSearch(a, searchQuery));
    }

    if (sortKey !== "default") {
      list = [...list].sort((a, b) => {
        if (sortKey === "name-asc")
          return (a.customer_name || "").localeCompare(b.customer_name || "");
        if (sortKey === "name-desc")
          return (b.customer_name || "").localeCompare(a.customer_name || "");
        if (sortKey === "ah-desc")
          return (b.account_health ?? -1) - (a.account_health ?? -1);
        if (sortKey === "renewal-asc")
          return (a.renewal_days ?? 9999) - (b.renewal_days ?? 9999);
        if (sortKey === "renewal-desc")
          return (b.renewal_days ?? -9999) - (a.renewal_days ?? -9999);
        if (sortKey === "revenue-desc")
          return (b.revenue ?? 0) - (a.revenue ?? 0);
        if (sortKey === "revenue-asc")
          return (a.revenue ?? 0) - (b.revenue ?? 0);
        if (sortKey === "al-desc")
          return (b.al_level ?? -1) - (a.al_level ?? -1);
        if (sortKey === "rl-desc")
          return (b.rl_aggregate ?? -1) - (a.rl_aggregate ?? -1);
        return 0;
      });
    }
    return list;
  }, [
    accounts,
    workingSetAccountIds,
    tileFilter,
    selectedTier,
    selectedRegion,
    selectedAl,
    healthMode,
    selectedCluster,
    selectedBhBand,
    searchQuery,
    sortKey,
    ahThresholds,
  ]);

  const clusterCounts = useMemo(() => {
    const counts: Record<string, number> = { all: accounts.length };
    for (const c of CLUSTER_ORDER) counts[c] = 0;
    accounts.forEach(a => {
      const c = clusterFromAccountHealth(
        a.account_health,
        a.customer_id,
        ahThresholds
      );
      if (counts[c] !== undefined) counts[c]++;
    });
    return counts;
  }, [accounts, ahThresholds]);

  const bhBandCounts = useMemo(() => {
    const counts: Record<"all" | BhBandKey, number> = {
      all: accounts.length,
      concern: 0,
      stable: 0,
      strong: 0,
      no_data: 0,
    };
    accounts.forEach(a => {
      counts[bhBandFromAccount(a.business_health)]++;
    });
    return counts;
  }, [accounts]);

  useEffect(() => {
    if (
      healthMode === "ah" &&
      selectedCluster !== "all" &&
      (clusterCounts[selectedCluster] ?? 0) === 0
    ) {
      setSelectedCluster("all");
    }
  }, [healthMode, selectedCluster, clusterCounts]);

  useEffect(() => {
    if (
      healthMode === "bh" &&
      selectedBhBand !== "all" &&
      (bhBandCounts[selectedBhBand] ?? 0) === 0
    ) {
      setSelectedBhBand("all");
    }
  }, [healthMode, selectedBhBand, bhBandCounts]);

  const selectHealthMode = useCallback((mode: HealthMode) => {
    setHealthMode(mode);
    setSelectedCluster("all");
    setSelectedBhBand("all");
  }, []);

  const grouped = useMemo(() => {
    const map: Record<ClusterKey, PortfolioAccountRow[]> = {
      triage: [],
      defend: [],
      grow: [],
      expand: [],
      unscored: [],
    };
    filtered.forEach(a => {
      const c = clusterFromAccountHealth(
        a.account_health,
        a.customer_id,
        ahThresholds
      );
      if (map[c]) map[c].push(a);
    });
    return map;
  }, [filtered, ahThresholds]);

  const groupedByBh = useMemo(() => {
    const map: Record<BhBandKey, PortfolioAccountRow[]> = {
      concern: [],
      stable: [],
      strong: [],
      no_data: [],
    };
    filtered.forEach(a => {
      map[bhBandFromAccount(a.business_health)].push(a);
    });
    return map;
  }, [filtered]);

  const hasActiveFilters =
    tileFilter != null ||
    healthMode !== "all" ||
    selectedCluster !== "all" ||
    selectedBhBand !== "all" ||
    Boolean(searchQuery.trim()) ||
    Boolean(selectedTier) ||
    Boolean(selectedRegion) ||
    Boolean(selectedAl);

  const panelFilterCount = (selectedTier ? 1 : 0) + (selectedRegion ? 1 : 0);

  const clearPanelFilters = useCallback(() => {
    setSelectedTier("");
    setSelectedRegion("");
  }, []);

  const emptyStateMessage = useMemo(() => {
    if (accounts.length === 0) {
      return {
        title: "No accounts in your portfolio",
        hint: "Accounts assigned to you will appear here.",
      };
    }
    if (tileFilter === "attention") {
      return {
        title: "No accounts need attention right now",
        hint: "That's a positive outcome — clear the filter or review all accounts.",
      };
    }
    if (tileFilter === "primary") {
      return {
        title: "No primary sponsor accounts",
        hint: "You have no accounts where you are the primary sponsor.",
      };
    }
    if (tileFilter === "secondary") {
      return {
        title: "No secondary sponsor accounts",
        hint: "You have no accounts where you are the secondary sponsor.",
      };
    }
    if (tileFilter === "executive") {
      return {
        title: "No additional sponsor accounts",
        hint: "You have no account-team or additional sponsor assignments.",
      };
    }
    if (healthMode === "ah" && selectedCluster !== "all") {
      const meta = CLUSTER_SECTION_META[selectedCluster as ClusterKey];
      const label = meta?.label ?? selectedCluster;
      const clusterTotal = clusterCounts[selectedCluster] ?? 0;
      return {
        title: `No ${label.toLowerCase()} accounts`,
        hint:
          clusterTotal === 0
            ? `You have no accounts in the ${label} cluster. Select All or another cluster to continue.`
            : "Try adjusting your search, tier, region, or level filters.",
      };
    }
    if (healthMode === "bh" && selectedBhBand !== "all") {
      const label = BH_BAND_META[selectedBhBand].label;
      const bandTotal = bhBandCounts[selectedBhBand] ?? 0;
      return {
        title: `No ${label.toLowerCase()} accounts`,
        hint:
          bandTotal === 0
            ? `You have no accounts in the ${label} Business Health band. Select All or another band to continue.`
            : "Try adjusting your search, tier, region, or level filters.",
      };
    }
    return {
      title: "No accounts match your filters",
      hint: "Try clearing search or adjusting tier, region, or level filters.",
    };
  }, [
    accounts.length,
    tileFilter,
    healthMode,
    selectedCluster,
    selectedBhBand,
    clusterCounts,
    bhBandCounts,
  ]);

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setSelectedTier("");
    setSelectedRegion("");
    setSelectedAl("");
    setSelectedCluster("all");
    setSelectedBhBand("all");
    setHealthMode("all");
    if (tileFilter) onClearTileFilter();
  }, [tileFilter, onClearTileFilter]);

  const renderRenewal = useCallback((a: PortfolioAccountRow) => {
    if (!a.renewal_date)
      return <span className="text-xs text-gray-400">—</span>;

    const dateStr = formatDateTime(a.renewal_date, "DMY_SHORT").date;
    const days = a.renewal_days;

    let daysLabel: string | null = null;
    if (days != null) {
      daysLabel = days < 0 ? `Due: ${days} Days` : `${days} Days remaining`;
    }

    return (
      <>
        <div className="text-xs font-medium text-gray-800">{dateStr}</div>
        {daysLabel ? (
          <div
            className={`mt-1 text-[10px] font-medium ${
              days != null && days < 0 ? "text-red-600" : "text-gray-400"
            }`}
          >
            {daysLabel}
          </div>
        ) : null}
      </>
    );
  }, []);

  const handlePairsClick = useCallback(
    (a: PortfolioAccountRow) => {
      const accountId = String(a.customer_id || "").trim();
      const name = (a.customer_name || "").trim();
      const label = name || accountId;
      // Match List "Pairs: mapped/cap" — account-scoped + my pairs only
      // (overview relationship_pairs also includes teammates' mappings).
      const accountPairs = pairs.filter(p => {
        if (!p.is_mine) return false;
        const pairAccountId = String(p.account_id || "").trim();
        if (accountId) return pairAccountId === accountId;
        const pairName = (p.account_name || "").trim();
        return Boolean(name && pairName === name);
      });
      const totalCount = accountPairs.length;
      const attentionCount = accountPairs.filter(p =>
        ATTENTION_BUCKETS.has(String(p.attention_bucket || ""))
      ).length;
      const stableCount = accountPairs.filter(
        p => String(p.attention_bucket || "") === "healthy"
      ).length;

      if (totalCount === 0) {
        setPairsFocus({
          kind: "no_mappings",
          account: a,
          label,
          attentionCount: 0,
          totalCount: 0,
          stableCount: 0,
        });
        return;
      }
      if (attentionCount === 0) {
        setPairsFocus({
          kind: "all_stable",
          account: a,
          label,
          attentionCount: 0,
          totalCount,
          stableCount,
        });
        return;
      }
      if (attentionCount === totalCount) {
        setPairsFocus({
          kind: "all_needs_attention",
          account: a,
          label,
          attentionCount,
          totalCount,
          stableCount: 0,
        });
        return;
      }
      setPairsFocus({
        kind: "mixed",
        account: a,
        label,
        attentionCount,
        totalCount,
        stableCount,
      });
    },
    [pairs]
  );

  const closePairsFocusModal = useCallback(() => setPairsFocus(null), []);

  const handlePairsFocusGoToRp = useCallback(() => {
    if (!pairsFocus) return;
    const account = pairsFocus.account;
    setPairsFocus(null);
    onGoToRelationshipPriorities?.(account, {
      suppressPairsFocusModal: true,
    });
  }, [pairsFocus, onGoToRelationshipPriorities]);

  const handlePairsFocusSeeAll = useCallback(() => {
    if (!pairsFocus) return;
    const accountId = String(pairsFocus.account.customer_id || "").trim();
    setPairsFocus(null);
    const params = new URLSearchParams({ tab: "account-relationships" });
    if (accountId) params.set("accountId", accountId);
    navigate(withOrg(`/relationship?${params.toString()}`));
  }, [pairsFocus, navigate, withOrg]);

  const renderPairs = (a: PortfolioAccountRow) => {
    const mapped = a.mapped_pairs;
    const cap =
      fanOutCap != null && fanOutCap > 0
        ? fanOutCap
        : a.total_possible_pairs || mapped || 0;
    const overLimit = cap > 0 && mapped > cap;
    const colorCls = overLimit ? "text-red-600" : "text-gray-900";
    const label = `Pairs: ${mapped}/${cap}`;
    if (!onGoToRelationshipPriorities) {
      return (
        <div
          className={`mb-0.5 inline-flex items-center gap-0.5 text-[11px] font-bold ${colorCls}`}
        >
          {label}
        </div>
      );
    }
    return (
      <button
        type="button"
        className={`mb-0.5 inline-flex cursor-pointer items-center gap-0.5 border-none bg-transparent p-0 text-[11px] font-bold hover:text-purple-600 hover:underline ${colorCls}`}
        title="View relationship pairs for this account"
        onClick={e => {
          e.stopPropagation();
          handlePairsClick(a);
        }}
      >
        {label}
      </button>
    );
  };

  const selectedIdSet = useMemo(
    () => new Set(selectedAccountIds),
    [selectedAccountIds]
  );
  const selectedAccounts = useMemo(
    () =>
      selectedAccountIds
        .map(id => accounts.find(a => a.customer_id === id))
        .filter((a): a is PortfolioAccountRow => a != null),
    [accounts, selectedAccountIds]
  );

  const selectionStripRef = useRef<HTMLDivElement>(null);
  const [stripCapacity, setStripCapacity] = useState(
    SELECTION_STRIP_FALLBACK_CAPACITY
  );

  useLayoutEffect(() => {
    if (!hasSelection) return;
    const el = selectionStripRef.current;
    if (!el) return;

    const measure = () => {
      const width = el.clientWidth;
      if (width <= 0) return;
      const slots = Math.max(1, Math.floor(width / SELECTION_AVATAR_SLOT_PX));
      setStripCapacity(slots);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasSelection, selectionBarDock, selectedAccounts.length]);

  // Fill available strip width; reserve one slot for "+N" when not all fit.
  const selectionStripVisibleCount =
    selectedAccounts.length <= stripCapacity
      ? selectedAccounts.length
      : Math.max(1, stripCapacity - 1);
  const selectionStripVisible = selectedAccounts.slice(
    0,
    selectionStripVisibleCount
  );
  const selectionStripOverflow = Math.max(
    0,
    selectedAccounts.length - selectionStripVisible.length
  );

  const toggleAccountSelection = useCallback((accountId: string) => {
    setSelectedAccountIds(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  }, []);

  const removeFromSelection = useCallback((accountId: string) => {
    setSelectedAccountIds(prev => prev.filter(id => id !== accountId));
  }, []);

  const clearSelection = useCallback(() => setSelectedAccountIds([]), []);

  const openSelectionOnMap = useCallback(() => {
    if (selectedAccountIds.length === 0) return;
    onOpenAccountsOnMap?.(selectedAccountIds);
  }, [onOpenAccountsOnMap, selectedAccountIds]);

  // Maps off — drop any leftover multi-select (nothing to open on Maps).
  useEffect(() => {
    if (onOpenAccountsOnMap) return;
    setSelectedAccountIds([]);
  }, [onOpenAccountsOnMap]);

  const portfolioNavState = {
    from: withOrg("/my-portfolio-accounts"),
    fromLabel: "My Accounts",
  };

  const goSynopsis = (a: PortfolioAccountRow) => {
    navigate(withOrg(`/account-synopsis/${a.customer_id}`), {
      state: portfolioNavState,
    });
  };

  const renderAccountCard = (a: PortfolioAccountRow) => {
    const canSelectForMaps = Boolean(onOpenAccountsOnMap);
    const isSelected = canSelectForMaps && selectedIdSet.has(a.customer_id);
    return (
      <div
        key={a.customer_id}
        {...(canSelectForMaps
          ? {
              role: "button" as const,
              tabIndex: 0,
              "aria-pressed": isSelected,
              onClick: () => toggleAccountSelection(a.customer_id),
              onKeyDown: (e: KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleAccountSelection(a.customer_id);
                }
              },
            }
          : {})}
        className={`${canSelectForMaps ? accountCardClass : accountCardStaticClass} ${CARD_GRID} ${
          isSelected
            ? accountCardSelectedClass
            : canSelectForMaps
              ? accountCardIdleClass
              : "border-gray-200/90"
        }`}
      >
        <div className="min-w-0 overflow-hidden self-center">
          <div className="grid min-w-0 grid-cols-[25px_minmax(0,1fr)] items-center gap-2 overflow-hidden">
            <span
              className="inline-flex h-[25px] w-[25px] items-center justify-center rounded-[5px] text-[10px] font-bold"
              style={
                getAccountIdentityBadgeStyle(a.customer_id, {
                  borderWidth: 1.25,
                  color: ACCOUNT_IDENTITY_FALLBACK,
                }) as CSSProperties
              }
              aria-hidden
            >
              {(getInitials(a.customer_name) || "?").slice(0, 1)}
            </span>
            <div className="min-w-0 overflow-hidden">
              <div
                className="overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] font-bold leading-tight text-gray-900"
                title={a.customer_name || undefined}
              >
                {a.customer_name}
              </div>
              <div className="mt-px overflow-hidden text-ellipsis whitespace-nowrap text-[10.5px] leading-snug text-gray-500">
                {[
                  a.industry || "—",
                  a.region || "—",
                  a.tier ? formatTier(a.tier) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </div>
        </div>
        <div className="flex min-w-0 w-full justify-center pr-2">
          <HealthMetricBubble
            score={a.business_health}
            variant="outlined"
            bhThresholds={bhThresholds}
            onClick={
              onOpenAccountOnMap
                ? () => onOpenAccountOnMap(a.customer_id, "business_health")
                : undefined
            }
          />
        </div>
        <div className="flex min-w-0 w-full justify-center pl-2">
          <HealthMetricBubble
            score={a.account_health}
            variant="solid"
            accountId={a.customer_id}
            ahThresholds={ahThresholds}
            onClick={
              onOpenAccountOnMap
                ? () => onOpenAccountOnMap(a.customer_id, "account_health")
                : undefined
            }
          />
        </div>
        <div className="min-w-0">
          {a.al_level != null || a.al_label ? (
            <>
              <div className="inline-flex items-center gap-0.5 text-[13px] font-bold text-gray-900">
                {a.al_level != null
                  ? formatAlLevel(a.al_level)
                  : formatAlLevel(
                      String(a.al_label).replace(/^AL\s*/i, "") || null
                    )}
                <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
              </div>
              {/* Temporarily hidden — restore when needed
              {a.al_published_days != null && (
                <div className="mt-px text-[10px] text-gray-400">
                  ({a.al_published_days} days)
                </div>
              )}
              */}
            </>
          ) : (
            <span
              className="text-[13px] font-bold text-gray-400"
              title={UNASSESSED_LEVEL_TITLE}
            >
              AL ?
            </span>
          )}
        </div>
        <div className="min-w-0">
          {renderPairs(a)}
          <div className="mt-1 whitespace-nowrap text-xs font-bold text-blue-700">
            My RL:{" "}
            {a.my_rl != null ? (
              `RL ${Math.round(a.my_rl)}`
            ) : (
              <span title={UNASSESSED_LEVEL_TITLE}>RL ?</span>
            )}
          </div>
          {/* Temporarily hidden — restore when needed
          {(a.pending_pairs > 0 ||
            (a.rl_band === "weak" && a.assessed_pairs > 0)) && (
            <div className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] text-red-600">
              <CornerDownRight className="h-3 w-3 shrink-0" aria-hidden />
              {[
                a.rl_band === "weak" ? "weak" : null,
                a.pending_pairs > 0 ? `${a.pending_pairs} unassessed` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
          */}
          <div className="mt-1 text-[11px] text-gray-400">
            Team RL:{" "}
            {a.rl_aggregate != null ? (
              `RL ${Number(a.rl_aggregate).toFixed(1)}`
            ) : (
              <span title={UNASSESSED_LEVEL_TITLE}>RL ?</span>
            )}
          </div>
          {/* Temporarily hidden — restore when needed
          {a.mapped_pairs === 0 && (
            <div className="mt-0.5">
              <span className="rounded-sm border border-red-200 bg-red-100 px-1.5 py-px text-[10px] font-bold text-red-600">
                Unmapped
              </span>
            </div>
          )}
          */}
        </div>
        <div className="min-w-0 overflow-hidden">{renderSponsorsCell(a)}</div>
        <div className="min-w-0">
          <strong className="text-[13px] text-gray-900">
            {formatCompactCurrency(a.revenue, { fallback: "—" })}
          </strong>
        </div>
        <div className="min-w-0">{renderRenewal(a)}</div>
        <div className="min-w-0">
          <div className="flex w-full flex-col items-stretch gap-2">
            <button
              type="button"
              className={synopsisBtn}
              onClick={e => {
                e.stopPropagation();
                goSynopsis(a);
              }}
            >
              Synopsis
              <ArrowRight className="h-3 w-3 shrink-0" />
            </button>
            <button
              type="button"
              className={assessBtn}
              onClick={e => {
                e.stopPropagation();
                onOpenAssess?.(a);
              }}
            >
              Assess
              <ArrowRight className="h-3 w-3 shrink-0" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const tierDropdownOptions = useMemo(
    () => [
      { label: "All Tiers", value: "" },
      ...tierOptions.map(t => ({ label: t.label, value: t.value })),
    ],
    [tierOptions]
  );

  const regionDropdownOptions = useMemo(
    () => [
      { label: "All Regions", value: "" },
      ...regionOptions.map(r => ({ label: r.label, value: r.value })),
    ],
    [regionOptions]
  );

  const levelSwitcherOptions = useMemo(
    () => [
      { value: "", label: "All Levels" },
      ...[1, 2, 3, 4, 5].map(n => ({ value: String(n), label: `AL ${n}` })),
      { value: "none", label: "AL ?" },
    ],
    []
  );

  const cycleColumnSort = useCallback(
    (ascKey: SortKey, descKey: SortKey, preferDescFirst = false) => {
      if (preferDescFirst) {
        if (sortKey === descKey) setSortKey(ascKey);
        else if (sortKey === ascKey) setSortKey("default");
        else setSortKey(descKey);
        return;
      }
      if (sortKey === ascKey) setSortKey(descKey);
      else if (sortKey === descKey) setSortKey("default");
      else setSortKey(ascKey);
    },
    [sortKey]
  );

  const headerFilters = headerFiltersRef?.current
    ? createPortal(
        <div className="flex flex-wrap items-center justify-end gap-2">
          {useSyntheticData ? (
            <div
              className="inline-flex shrink-0 items-center gap-1 text-[11.5px] font-medium text-amber-700"
              role="status"
            >
              <span className="whitespace-nowrap">
                Synthetic Data Mode is enabled.
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                    aria-label="About Synthetic Data Mode"
                  >
                    <Info
                      className="h-2.5 w-2.5"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="end"
                  sideOffset={8}
                  className="z-[9999] max-w-[280px] border border-gray-200 bg-white px-[11px] py-2 text-left text-[11.5px] font-normal leading-relaxed text-gray-800 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
                >
                  KPIs and metrics displayed are based on sample data, not live
                  production data.
                </TooltipContent>
              </Tooltip>
            </div>
          ) : null}
          <DropdownSwitcher
            className="shrink-0"
            value={selectedAl}
            prefix="Levels: "
            options={levelSwitcherOptions}
            onChange={setSelectedAl}
            ariaLabel={`Levels: ${
              levelSwitcherOptions.find(o => o.value === selectedAl)?.label ??
              "All Levels"
            }. Change levels filter.`}
            listboxLabel="Levels"
          />
          <div className="w-[200px] shrink-0">
            <SearchInput
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              rounded="normal"
              size="sm"
            />
          </div>
          <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900 ${
                  filtersOpen ? "border-gray-300 text-gray-900" : ""
                }`}
                aria-label="Filters"
                aria-expanded={filtersOpen}
                aria-haspopup="dialog"
              >
                <Filter className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                {panelFilterCount > 0 ? (
                  <span className="absolute right-0 top-0 inline-flex h-[14px] min-w-[14px] translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full bg-brand px-1 text-[9px] font-bold leading-none text-white">
                    {panelFilterCount}
                  </span>
                ) : null}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="z-[9999] w-[280px] rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
              onInteractOutside={event => {
                const target = event.target as Element | null;
                if (target?.closest?.(".dropdown-selector-menu")) {
                  event.preventDefault();
                }
              }}
              onFocusOutside={event => {
                const target = event.target as Element | null;
                if (target?.closest?.(".dropdown-selector-menu")) {
                  event.preventDefault();
                }
              }}
            >
              <div className="mb-2.5 flex items-center justify-between gap-2">
                <p className="text-[12.5px] font-bold text-gray-900">Filters</p>
                {panelFilterCount > 0 ? (
                  <button
                    type="button"
                    className="border-none bg-transparent p-0 text-[11.5px] font-semibold text-brand hover:underline"
                    onClick={clearPanelFilters}
                  >
                    Clear all
                  </button>
                ) : null}
              </div>
              <div className="flex flex-col gap-2.5">
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Tier
                  </span>
                  <DropdownSelector
                    label="All Tiers"
                    selected={selectedTier || null}
                    options={tierDropdownOptions}
                    onSelect={v => setSelectedTier(v)}
                    compact
                    fullWidth
                    triggerStyle="field"
                    hideLabel
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Region
                  </span>
                  <DropdownSelector
                    label="All Regions"
                    selected={selectedRegion || null}
                    options={regionDropdownOptions}
                    onSelect={v => setSelectedRegion(v)}
                    compact
                    fullWidth
                    triggerStyle="field"
                    hideLabel
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>,
        headerFiltersRef.current
      )
    : null;

  const mapsClickable = Boolean(onOpenAccountOnMap);

  const prioritiesFilterBar = (
    <>
      {/* Only when Maps are enabled — omit when admin has Maps disabled. */}
      {mapsConfigReady && mapsClickable ? (
        <p className="m-0 hidden shrink-0 text-xs italic text-gray-400 sm:block">
          Cards are clickable to open on Maps
        </p>
      ) : null}
      <div
        className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg border border-gray-200 bg-gray-100 p-0.5"
        role="tablist"
        aria-label="Account health filters"
      >
        <div
          className={`inline-flex items-center gap-0.5 rounded-md px-0.5 py-px transition-colors ${
            healthMode === "all" ? "bg-white shadow-sm" : ""
          }`}
        >
          <button
            type="button"
            role="tab"
            aria-selected={healthMode === "all"}
            className={`inline-flex items-center gap-1.5 rounded border-none bg-transparent px-1.5 py-1.5 text-xs font-semibold transition-colors ${
              healthMode === "all"
                ? "text-[#7a36c4]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => selectHealthMode("all")}
          >
            <LayoutGrid className="h-3.5 w-3.5 opacity-85" aria-hidden />
            All
          </button>
        </div>

        <div
          className="mx-0.5 my-1 w-px shrink-0 self-stretch bg-gray-200"
          aria-hidden
        />

        <div
          className={`inline-flex items-center gap-0.5 rounded-md px-0.5 py-px transition-colors ${
            healthMode === "bh" ? "bg-white shadow-sm" : ""
          }`}
        >
          <div className="inline-flex items-center gap-1 whitespace-nowrap px-1">
            <button
              type="button"
              role="tab"
              aria-selected={healthMode === "bh"}
              className={`inline-flex items-center gap-1.5 rounded border-none bg-transparent px-1.5 py-1.5 text-xs font-semibold transition-colors ${
                healthMode === "bh"
                  ? "text-[#7a36c4]"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => selectHealthMode("bh")}
            >
              <Activity className="h-3.5 w-3.5 opacity-85" aria-hidden />
              Business Health
            </button>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={headerInfoIconClass}
                  aria-label="About Business Health"
                  onClick={e => e.stopPropagation()}
                >
                  <Info className="h-2 w-2" strokeWidth={2.5} aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                sideOffset={8}
                className={headerTooltipClass}
              >
                {headerInfoTooltips.businessHealth}
              </TooltipContent>
            </Tooltip>
          </div>
          {healthMode === "bh" ? (
            <div
              className="inline-flex items-center gap-px pr-1"
              role="group"
              aria-label="Business Health bands"
            >
              {BH_BAND_ORDER.map((band, i) => {
                const count = bhBandCounts[band] ?? 0;
                const disabled = count === 0;
                const active = selectedBhBand === band && !disabled;
                return (
                  <Fragment key={band}>
                    {i > 0 && (
                      <span
                        className="select-none px-0.5 text-[11px] font-normal text-gray-300"
                        aria-hidden
                      >
                        |
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={disabled}
                      aria-pressed={active}
                      title={
                        disabled
                          ? chipDisabledTooltip(BH_BAND_META[band].label)
                          : undefined
                      }
                      className={`whitespace-nowrap rounded border-none px-1.5 py-0.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                        active
                          ? "bg-[#f5edfc] text-brand"
                          : "bg-transparent text-gray-500 hover:bg-black/[0.04] hover:text-gray-700"
                      }`}
                      onClick={() =>
                        setSelectedBhBand(prev =>
                          prev === band ? "all" : band
                        )
                      }
                    >
                      {BH_BAND_META[band].label} · {count}
                    </button>
                  </Fragment>
                );
              })}
            </div>
          ) : null}
        </div>

        <div
          className="mx-0.5 my-1 w-px shrink-0 self-stretch bg-gray-200"
          aria-hidden
        />

        <div
          className={`inline-flex items-center gap-0.5 rounded-md px-0.5 py-px transition-colors ${
            healthMode === "ah" ? "bg-white shadow-sm" : ""
          }`}
        >
          <div className="inline-flex items-center gap-1 whitespace-nowrap px-1">
            <button
              type="button"
              role="tab"
              aria-selected={healthMode === "ah"}
              className={`inline-flex items-center gap-1.5 rounded border-none bg-transparent px-1.5 py-1.5 text-xs font-semibold transition-colors ${
                healthMode === "ah"
                  ? "text-[#7a36c4]"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => selectHealthMode("ah")}
            >
              <HeartPulse className="h-3.5 w-3.5 opacity-85" aria-hidden />
              Account Health
            </button>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={headerInfoIconClass}
                  aria-label="About Account Health"
                  onClick={e => e.stopPropagation()}
                >
                  <Info className="h-2 w-2" strokeWidth={2.5} aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                sideOffset={8}
                className={headerTooltipClass}
              >
                {headerInfoTooltips.accountHealth}
              </TooltipContent>
            </Tooltip>
          </div>
          {healthMode === "ah" ? (
            <div
              className="inline-flex items-center gap-px pr-1"
              role="group"
              aria-label="Account Health clusters"
            >
              {CLUSTER_ORDER.map((c, i) => {
                const count = clusterCounts[c] ?? 0;
                const disabled = count === 0;
                const active = selectedCluster === c && !disabled;
                const chipLabel = CLUSTER_SECTION_META[c].label;
                return (
                  <Fragment key={c}>
                    {i > 0 && (
                      <span
                        className="select-none px-0.5 text-[11px] font-normal text-gray-300"
                        aria-hidden
                      >
                        |
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={disabled}
                      aria-pressed={active}
                      title={
                        disabled ? chipDisabledTooltip(chipLabel) : undefined
                      }
                      className={`whitespace-nowrap rounded border-none px-1.5 py-0.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                        active
                          ? "bg-[#f5edfc] text-brand"
                          : "bg-transparent text-gray-500 hover:bg-black/[0.04] hover:text-gray-700"
                      }`}
                      onClick={() =>
                        setSelectedCluster(prev => (prev === c ? "all" : c))
                      }
                    >
                      {chipLabel} · {count}
                    </button>
                  </Fragment>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  const prioritiesFilterBarPortal = prioritiesFilterBarEl
    ? createPortal(prioritiesFilterBar, prioritiesFilterBarEl)
    : null;

  if (loading) {
    return (
      <TooltipProvider delayDuration={150}>
        {prioritiesFilterBarPortal}
        <div className="flex min-h-[320px] flex-col items-center justify-center text-gray-500">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-400" />
          <p className="mt-2">Loading accounts...</p>
        </div>
      </TooltipProvider>
    );
  }

  const columnHeaders = (
    <div className={columnHeaderRowClass}>
      <div className={colHeaderClass}>
        <SortableColHeader
          label="Account"
          activeDir={
            sortKey === "name-asc"
              ? "asc"
              : sortKey === "name-desc"
                ? "desc"
                : null
          }
          onClick={() => cycleColumnSort("name-asc", "name-desc")}
        />
      </div>
      <div className={`${colHeaderClass} flex justify-center pr-2 text-center`}>
        <TableHeaderWithInfo
          label="Business Health"
          tooltip={headerInfoTooltips.businessHealth}
          centered
        />
      </div>
      <div className={`${colHeaderClass} flex justify-center pl-2 text-center`}>
        <TableHeaderWithInfo
          label="Account Health"
          tooltip={headerInfoTooltips.accountHealth}
          centered
        />
      </div>
      <div className={colHeaderClass}>Account Level</div>
      <div className={colHeaderClass}>
        <TableHeaderWithInfo
          label="Relationships"
          tooltip={headerInfoTooltips.relationships}
        />
      </div>
      <div className={colHeaderClass}>Sponsors</div>
      <div className={colHeaderClass}>
        <SortableColHeader
          label="Revenue"
          activeDir={
            sortKey === "revenue-asc"
              ? "asc"
              : sortKey === "revenue-desc"
                ? "desc"
                : null
          }
          onClick={() => cycleColumnSort("revenue-asc", "revenue-desc", true)}
        />
      </div>
      <div className={colHeaderClass}>
        <SortableColHeader
          label="Renewal"
          activeDir={
            sortKey === "renewal-asc"
              ? "asc"
              : sortKey === "renewal-desc"
                ? "desc"
                : null
          }
          onClick={() => cycleColumnSort("renewal-asc", "renewal-desc")}
        />
      </div>
      <div className={colHeaderClass}>Actions</div>
    </div>
  );

  const renderClusteredCards = () => {
    if (healthMode === "bh") {
      return (
        <div className={accountCardsListClass}>
          {BH_BAND_ORDER.flatMap(band =>
            groupedByBh[band].map(a => renderAccountCard(a))
          )}
        </div>
      );
    }
    return (
      <div className={accountCardsListClass}>
        {CLUSTER_ORDER.flatMap(cluster =>
          grouped[cluster].map(a => renderAccountCard(a))
        )}
      </div>
    );
  };

  const accountCardList = (
    <div className={listShellClass}>
      <div className="min-w-0 overflow-x-auto">
        {columnHeaders}
        {sortKey === "default" ? (
          renderClusteredCards()
        ) : (
          <div className={accountCardsListClass}>
            {filtered.map(a => renderAccountCard(a))}
          </div>
        )}
      </div>
    </div>
  );

  const emptyStatePanel = (
    <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-white px-6 py-10 text-center text-gray-500">
      <p className="text-sm font-medium text-gray-600">
        {emptyStateMessage.title}
      </p>
      <p className="mt-1 max-w-md text-xs text-gray-400">
        {emptyStateMessage.hint}
      </p>
      {hasActiveFilters && (
        <button
          type="button"
          className="mt-4 cursor-pointer rounded-md border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 transition-colors hover:bg-purple-100"
          onClick={clearFilters}
        >
          Clear filters
        </button>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div
        ref={selectionBoundsRef}
        style={{
          paddingBottom: hasSelection ? "4.75rem" : undefined,
        }}
      >
        {headerFilters}
        {prioritiesFilterBarPortal}

        {workingSetAccountIds && onClearWorkingSet && (
          <WorkingSetBanner
            count={workingSetAccountIds.length}
            onClear={onClearWorkingSet}
          />
        )}

        {tileFilter && (
          <div className="mb-2.5">
            <span className="mb-2.5 inline-flex items-center gap-1.5 rounded-2xl border border-gray-300 bg-gray-100 py-0.5 pl-3 pr-2.5 text-[11.5px] font-bold text-gray-700">
              <span>{TILE_FILTER_LABELS[tileFilter]}</span>
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent p-0 text-[13px] leading-none text-gray-600"
                onClick={onClearTileFilter}
                title="Clear filter"
              >
                ✕
              </button>
            </span>
          </div>
        )}

        {filtered.length > 0 ? accountCardList : emptyStatePanel}

        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1.5 py-2.5 text-[11.5px] leading-none text-gray-400">
          <span className="leading-none">Filter chips narrow the list</span>
          <span className="inline-flex items-center gap-1 leading-none">
            <strong className="font-semibold leading-none text-gray-700">
              Synopsis
            </strong>
            <ArrowRight
              className="relative top-px h-3 w-3 shrink-0 text-gray-500"
              strokeWidth={2.25}
              aria-hidden
            />
            <span className="leading-none">opens Account Synopsis</span>
          </span>
          <span className="inline-flex items-center gap-1 leading-none">
            <strong className="font-semibold leading-none text-gray-700">
              Assess
            </strong>
            <ArrowRight
              className="relative top-px h-3 w-3 shrink-0 text-gray-500"
              strokeWidth={2.25}
              aria-hidden
            />
            <span className="leading-none">starts team assessment</span>
          </span>
        </div>
      </div>

      {hasSelection &&
        selectionBarDock &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[9990]"
            style={{
              left: selectionBarDock.left,
              width: selectionBarDock.width,
              bottom: selectionBarDock.bottom,
            }}
          >
            <div className={FLOATING_SELECTION_BAR_PANEL}>
              <ShoppingBag
                className="h-5 w-5 shrink-0 text-slate-400"
                strokeWidth={1.75}
                aria-hidden
              />
              <div
                ref={selectionStripRef}
                className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden py-0.5"
              >
                {selectionStripVisible.map(a => (
                  <button
                    key={a.customer_id}
                    type="button"
                    onClick={() => removeFromSelection(a.customer_id)}
                    className="group relative shrink-0"
                    title={`Remove ${a.customer_name} from selection`}
                    aria-label={`Remove ${a.customer_name} from selection`}
                  >
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-full border-2 text-[10px] font-bold leading-none sm:h-10 sm:w-10 sm:text-[11px]"
                      style={
                        getAccountIdentityBadgeStyle(a.customer_id, {
                          borderWidth: 2,
                          color: ACCOUNT_IDENTITY_FALLBACK,
                        }) as CSSProperties
                      }
                    >
                      {(getInitials(a.customer_name) || "?").slice(0, 1)}
                    </span>
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-white shadow ring-2 ring-white transition-opacity group-hover:bg-rose-600">
                      <X className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                  </button>
                ))}
                {selectionStripOverflow > 0 ? (
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-slate-50 text-[10px] font-bold text-slate-600 sm:h-10 sm:w-10 sm:text-[11px]"
                    title={`${selectionStripOverflow} more selected`}
                  >
                    +{selectionStripOverflow}
                  </span>
                ) : null}
              </div>
              <div className="ml-auto flex shrink-0 items-center gap-2">
                {onOpenAccountsOnMap ? (
                  <button
                    type="button"
                    onClick={openSelectionOnMap}
                    className={selectionBarOutlineBtn}
                    title="Open Maps with selected accounts"
                  >
                    <MapIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                    Maps
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-[12px] font-semibold text-violet-700 hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <PairsFocusModal
        kind={pairsFocus?.kind ?? null}
        accountLabel={pairsFocus?.label ?? ""}
        attentionCount={pairsFocus?.attentionCount}
        totalCount={pairsFocus?.totalCount}
        stableCount={pairsFocus?.stableCount}
        onStayHere={closePairsFocusModal}
        onGoToRelationshipPriorities={handlePairsFocusGoToRp}
        onSeeAllRelationships={handlePairsFocusSeeAll}
      />
    </TooltipProvider>
  );
};

export default AccountPrioritiesTab;
